/* WI-12031 PR 2 — modal forms that wire the existing pages to backend mutations.
 *
 * One file (vs. one-per-modal) because they share a small set of primitives
 * (Modal shell, FieldRow) and the per-modal logic is short. Splitting per file
 * would be ~10 imports each, no gain.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { X } from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'
import type {
  AnalyzedCompany, DiscountRule, DiscountRuleType, ProductArticle,
  Subscription, SubscriptionLine,
} from '@/types/api'


// ── Shared shell ────────────────────────────────────────────────────────────


export function Modal({
  title, subtitle, onClose, children, footer, width = 540,
}: {
  title: string
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  useEffect(() => {
    function esc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'color-mix(in oklch, var(--ns-text) 30%, transparent)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="card"
        style={{ maxWidth: width, width: '100%', padding: 22, boxShadow: 'var(--ns-shadow)' }}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{subtitle}</div>}
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {children}
        </div>
        {footer && (
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}


export function FieldRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
      {children}
      {hint && <span className="muted" style={{ fontSize: 11.5 }}>{hint}</span>}
    </label>
  )
}


function mutationError(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback
}


// ── AddLineModal — POST /subscriptions/{id}/lines ──────────────────────────


export function AddLineModal({
  subscription, onClose,
}: { subscription: Subscription; onClose: () => void }) {
  const qc = useQueryClient()
  const [companyId,    setCompanyId]    = useState<number | ''>('')
  const [articleId,    setArticleId]    = useState<number | ''>('')
  const [unitPrice,    setUnitPrice]    = useState<string>('')
  const [validFrom,    setValidFrom]    = useState<string>(subscription.commitmentStart)
  const [trialEndDate, setTrialEndDate] = useState<string>('')

  // Inline "create new analyzed company" state. When the user picks the
  // sentinel "__new__" value from the dropdown we reveal a small form that
  // POSTs to /analyzed-companies and re-selects the new row on success.
  // Per the WI handoff: "allow creation via the UI when adding a subscription
  // line — Create new company inline."
  const [showNewCompany, setShowNewCompany] = useState(false)
  const [newCoExtId,     setNewCoExtId]     = useState('')
  const [newCoName,      setNewCoName]      = useState('')
  const [newCoCountry,   setNewCoCountry]   = useState('')

  const { data: companies = [] } = useQuery<AnalyzedCompany[]>({
    queryKey: ['analyzed-companies'],
    queryFn:  () => api.get('/analyzed-companies'),
  })
  const { data: articles = [] } = useQuery<ProductArticle[]>({
    queryKey: ['articles', 'true'],
    queryFn:  () => api.get('/articles?isProduct=true'),
  })

  const createCompanyMut = useMutation<{ id: number }, ApiError>({
    mutationFn: async () => api.post('/analyzed-companies', {
      externalIdentifier: newCoExtId.trim(),
      name:               newCoName.trim(),
      country:            newCoCountry.trim() || null,
    }),
    onSuccess: (res) => {
      toast.success(`Company "${newCoName.trim()}" created`)
      qc.invalidateQueries({ queryKey: ['analyzed-companies'] })
      // Auto-select the new row so the user doesn't have to find it.
      setCompanyId(res.id)
      setShowNewCompany(false)
      setNewCoExtId(''); setNewCoName(''); setNewCoCountry('')
    },
    onError: e => toast.error(mutationError(e, 'Could not create company')),
  })

  const mutation = useMutation({
    mutationFn: async () => api.post(`/subscriptions/${subscription.id}/lines`, {
      analyzedCompanyId: companyId,
      productArticleId:  articleId,
      unitPrice:         Number(unitPrice),
      validFrom,
      trialEndDate:      trialEndDate || null,
    }),
    onSuccess: () => {
      toast.success('Line added')
      qc.invalidateQueries({ queryKey: ['subscription-lines', subscription.id] })
      qc.invalidateQueries({ queryKey: ['subscriptions']                       })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not add line')),
  })

  const canSubmit = companyId !== '' && articleId !== '' && unitPrice !== '' && validFrom !== ''
  const canCreateCompany = newCoExtId.trim() !== '' && newCoName.trim() !== ''

  function handleCompanyChange(value: string) {
    if (value === '__new__') {
      setShowNewCompany(true)
      setCompanyId('')
    } else {
      setShowNewCompany(false)
      setCompanyId(Number(value) || '')
    }
  }

  return (
    <Modal
      title="Add line to subscription"
      subtitle={<>{subscription.customerName} · {subscription.name}</>}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand"
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >{mutation.isPending ? 'Adding…' : 'Add line'}</button>
        </>
      }
    >
      <FieldRow label="Analyzed company">
        <select
          className="input"
          value={showNewCompany ? '__new__' : companyId}
          onChange={e => handleCompanyChange(e.target.value)}
        >
          <option value="">— pick a company —</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.externalIdentifier})</option>
          ))}
          <option value="__new__">+ New analyzed company…</option>
        </select>
      </FieldRow>

      {/* Inline create-company sub-form. Shows below the dropdown when the
          user picks "+ New analyzed company…". POSTing /analyzed-companies
          auto-selects the new row in the dropdown above. */}
      {showNewCompany && (
        <div className="card" style={{ padding: 12, background: 'var(--ns-surface-2)', borderLeft: '3px solid var(--ns-brand-strong)' }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            Create a new analyzed company
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 8 }}>
            <FieldRow label="External ID" hint="Clementine identifier (org no, internal ID).">
              <input
                className="input mono"
                value={newCoExtId}
                onChange={e => setNewCoExtId(e.target.value)}
                placeholder="556677-8899"
              />
            </FieldRow>
            <FieldRow label="Name">
              <input
                className="input"
                value={newCoName}
                onChange={e => setNewCoName(e.target.value)}
                placeholder="ACME Gothenburg West"
              />
            </FieldRow>
            <FieldRow label="Country (optional)">
              <input
                className="input"
                maxLength={60}
                value={newCoCountry}
                onChange={e => setNewCoCountry(e.target.value)}
                placeholder="SE"
              />
            </FieldRow>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={() => setShowNewCompany(false)}>Cancel</button>
            <button
              className="btn btn-brand btn-sm"
              disabled={!canCreateCompany || createCompanyMut.isPending}
              onClick={() => createCompanyMut.mutate()}
            >
              {createCompanyMut.isPending ? 'Creating…' : 'Create company'}
            </button>
          </div>
        </div>
      )}

      <FieldRow label="Product article">
        <select
          className="input"
          value={articleId}
          onChange={e => setArticleId(Number(e.target.value) || '')}
        >
          <option value="">— pick an article —</option>
          {articles.filter(a => a.isActiveInFortnox).map(a => (
            <option key={a.id} value={a.id}>
              {a.articleNumber} — {a.name} ({a.productType ?? '—'})
            </option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label={`Unit price (${subscription.currency} / month)`}>
        <input
          type="number" min="0" step="0.01"
          className="input"
          value={unitPrice}
          onChange={e => setUnitPrice(e.target.value)}
        />
      </FieldRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Valid from">
          <input type="date" className="input" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
        </FieldRow>
        <FieldRow label="Trial end (optional)">
          <input type="date" className="input" value={trialEndDate} onChange={e => setTrialEndDate(e.target.value)} />
        </FieldRow>
      </div>
    </Modal>
  )
}


// ── ChangePriceModal — POST /subscription-lines/{id}/change-price ──────────


export function ChangePriceModal({
  line, currency, onClose,
}: { line: SubscriptionLine; currency: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [newUnitPrice, setNewUnitPrice] = useState<string>(line.currentUnitPrice?.toString() ?? '')
  const [validFrom,    setValidFrom]    = useState<string>(new Date().toISOString().slice(0, 10))
  const [changeReason, setChangeReason] = useState<string>('NEGOTIATED')
  const [changeNote,   setChangeNote]   = useState<string>('')

  const mutation = useMutation({
    mutationFn: async () => api.post(`/subscription-lines/${line.id}/change-price`, {
      newUnitPrice: Number(newUnitPrice),
      validFrom,
      changeReason,
      changeNote: changeNote || null,
    }),
    onSuccess: () => {
      toast.success('Price changed — new period opens ' + validFrom)
      qc.invalidateQueries({ queryKey: ['subscription-lines', line.subscriptionId] })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not change price')),
  })

  return (
    <Modal
      title="Change line price"
      subtitle={<>{line.analyzedCompanyName} · {line.articleNumber}</>}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand"
            disabled={mutation.isPending || !newUnitPrice || !validFrom}
            onClick={() => mutation.mutate()}
          >{mutation.isPending ? 'Saving…' : 'Apply'}</button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 12.5 }}>
        Current price: <strong className="tabular">{line.currentUnitPrice} {currency}/month</strong>
      </div>
      <FieldRow label={`New unit price (${currency} / month)`}>
        <input type="number" min="0" step="0.01" className="input"
          value={newUnitPrice} onChange={e => setNewUnitPrice(e.target.value)} />
      </FieldRow>
      <FieldRow label="Effective from" hint="The current period closes the day before; a new period opens on this date.">
        <input type="date" className="input" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
      </FieldRow>
      <FieldRow label="Reason">
        <select className="input" value={changeReason} onChange={e => setChangeReason(e.target.value)}>
          <option value="NEGOTIATED">Negotiated</option>
          <option value="INDEXATION">Annual indexation</option>
          <option value="MANUAL_ADJUSTMENT">Manual adjustment</option>
        </select>
      </FieldRow>
      <FieldRow label="Note (optional)">
        <textarea className="input" rows={2} value={changeNote} onChange={e => setChangeNote(e.target.value)} />
      </FieldRow>
    </Modal>
  )
}


// ── EndLineModal — POST /subscription-lines/{id}/end ───────────────────────


export function EndLineModal({
  line, onClose,
}: { line: SubscriptionLine; onClose: () => void }) {
  const qc = useQueryClient()
  const [validTo, setValidTo] = useState<string>(new Date().toISOString().slice(0, 10))

  const mutation = useMutation({
    mutationFn: async () => api.post(`/subscription-lines/${line.id}/end`, { validTo }),
    onSuccess: () => {
      toast.success('Line ended ' + validTo)
      qc.invalidateQueries({ queryKey: ['subscription-lines', line.subscriptionId] })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not end line')),
  })

  return (
    <Modal
      title="End line"
      subtitle={<>{line.analyzedCompanyName} · {line.articleNumber}</>}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn"
            style={{ color: 'var(--ns-err)', borderColor: 'var(--ns-err)' }}
            disabled={mutation.isPending || !validTo}
            onClick={() => mutation.mutate()}
          >{mutation.isPending ? 'Ending…' : 'End line'}</button>
        </>
      }
    >
      <FieldRow label="Effective end date" hint="The line bills through this date (inclusive). The current period closes on the same day.">
        <input type="date" className="input" value={validTo} onChange={e => setValidTo(e.target.value)} />
      </FieldRow>
    </Modal>
  )
}


// ── PauseSubscriptionModal — POST /subscription-pauses ─────────────────────


export function PauseSubscriptionModal({
  subscription, onClose,
}: { subscription: Subscription; onClose: () => void }) {
  const qc = useQueryClient()
  const [validFrom, setValidFrom] = useState<string>(new Date().toISOString().slice(0, 10))
  const [validTo,   setValidTo]   = useState<string>('')
  const [reason,    setReason]    = useState<string>('')

  const mutation = useMutation({
    mutationFn: async () => {
      await api.post('/subscription-pauses', {
        subscriptionId: subscription.id,
        validFrom,
        validTo: validTo || null,
        reason:  reason || null,
      })
      // Flip the parent subscription's status so the UI reflects the pause.
      await api.post(`/subscriptions/${subscription.id}/pause`, {})
    },
    onSuccess: () => {
      toast.success('Subscription paused')
      qc.invalidateQueries({ queryKey: ['subscriptions']                          })
      qc.invalidateQueries({ queryKey: ['subscription-lines', subscription.id]    })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not pause subscription')),
  })

  return (
    <Modal
      title="Pause subscription"
      subtitle={<>{subscription.customerName} · {subscription.name}</>}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand" disabled={mutation.isPending || !validFrom}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Pausing…' : 'Pause'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Pause from">
          <input type="date" className="input" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
        </FieldRow>
        <FieldRow label="Pause until (optional)" hint="Leave blank for indefinite pause.">
          <input type="date" className="input" value={validTo} onChange={e => setValidTo(e.target.value)} />
        </FieldRow>
      </div>
      <FieldRow label="Reason" hint="Captured in the audit trail and invoice generation skips paused days automatically.">
        <textarea className="input" rows={3} value={reason} onChange={e => setReason(e.target.value)} />
      </FieldRow>
    </Modal>
  )
}


// ── ApplyDiscountModal — POST /subscriptions/{id}/discounts ────────────────


export function ApplyDiscountModal({
  subscription, lineId, onClose,
}: { subscription: Subscription; lineId?: number; onClose: () => void }) {
  const qc = useQueryClient()
  const [ruleId,    setRuleId]    = useState<number | ''>('')
  const [scope,     setScope]     = useState<'subscription' | 'line' | 'product'>(lineId ? 'line' : 'subscription')
  const [articleId, setArticleId] = useState<number | ''>('')
  const [validFrom, setValidFrom] = useState<string>(new Date().toISOString().slice(0, 10))
  const [validTo,   setValidTo]   = useState<string>('')

  const { data: rules = [] } = useQuery<DiscountRule[]>({
    queryKey: ['discount-rules'],
    queryFn:  () => api.get('/discount-rules'),
  })
  const { data: articles = [] } = useQuery<ProductArticle[]>({
    queryKey: ['articles', 'true'],
    queryFn:  () => api.get('/articles?isProduct=true'),
  })

  const selectedRule = rules.find(r => r.id === ruleId)
  const isVolumeTier = selectedRule?.ruleType === 'VOLUME_TIER'
  // VOLUME_TIER rules cannot be line-scoped — enforce in the UI to match the backend.
  const allowedScopes: typeof scope[] = isVolumeTier
    ? ['subscription', 'product']
    : ['subscription', 'line', 'product']

  const mutation = useMutation({
    mutationFn: async () => api.post(`/subscriptions/${subscription.id}/discounts`, {
      discountRuleId:       ruleId,
      subscriptionLineId:   scope === 'line'    ? lineId    : null,
      productArticleId:     scope === 'product' ? articleId : null,
      validFrom,
      validTo: validTo || null,
    }),
    onSuccess: () => {
      toast.success('Discount attached')
      qc.invalidateQueries({ queryKey: ['subscription-discounts', subscription.id] })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not attach discount')),
  })

  return (
    <Modal
      title="Apply discount"
      subtitle={<>{subscription.customerName} · {subscription.name}</>}
      width={580}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand"
            disabled={mutation.isPending || ruleId === '' || (scope === 'product' && articleId === '')}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Attaching…' : 'Attach'}
          </button>
        </>
      }
    >
      <FieldRow label="Rule">
        <select className="input" value={ruleId} onChange={e => setRuleId(Number(e.target.value) || '')}>
          <option value="">— pick a rule —</option>
          {rules.filter(r => r.archivedAt == null).map(r => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.ruleType})
            </option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label="Scope">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {allowedScopes.map(s => (
            <button
              key={s}
              type="button"
              className={`btn btn-sm ${scope === s ? 'btn-brand' : ''}`}
              onClick={() => setScope(s)}
            >
              {s === 'subscription' ? 'Entire subscription'
                : s === 'line'      ? 'Single line'
                : 'Product type'}
            </button>
          ))}
        </div>
      </FieldRow>
      {scope === 'product' && (
        <FieldRow label="Product article" hint="The discount narrows to lines using this article's product type.">
          <select className="input" value={articleId} onChange={e => setArticleId(Number(e.target.value) || '')}>
            <option value="">— pick —</option>
            {articles.map(a => (
              <option key={a.id} value={a.id}>{a.productType ?? '—'} · {a.articleNumber}</option>
            ))}
          </select>
        </FieldRow>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Valid from">
          <input type="date" className="input" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
        </FieldRow>
        <FieldRow label="Valid to (optional)">
          <input type="date" className="input" value={validTo} onChange={e => setValidTo(e.target.value)} />
        </FieldRow>
      </div>
    </Modal>
  )
}


// ── NewSubscriptionModal — POST /subscriptions ────────────────────────────
// Single-step minimal form (the 4-step wizard from the design is a PR 2 stretch;
// landing the create-flow at all is the priority).


export function NewSubscriptionModal({
  defaultCustomerId, customers, onClose,
}: {
  defaultCustomerId?: number
  customers: { id: number; name: string }[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [customerId,         setCustomerId]         = useState<number | ''>(defaultCustomerId ?? '')
  const [name,               setName]               = useState<string>('')
  const [commitmentStart,    setCommitmentStart]    = useState<string>('')
  const [commitmentEnd,      setCommitmentEnd]      = useState<string>('')
  const [billingCycle,       setBillingCycle]       = useState<string>('MONTHLY')
  const [billingMode,        setBillingMode]        = useState<string>('ARREARS')
  const [invoiceGrouping,    setInvoiceGrouping]    = useState<string>('ONE_INVOICE')
  const [contractReference,  setContractReference]  = useState<string>('')
  const [noticePeriodDays,   setNoticePeriodDays]   = useState<string>('')

  const mutation = useMutation({
    mutationFn: async () => api.post('/subscriptions', {
      customerId, name,
      commitmentStart,
      commitmentEnd:    commitmentEnd || null,
      billingCycle, billingMode, invoiceGrouping,
      contractReference: contractReference || null,
      noticePeriodDays:  noticePeriodDays ? Number(noticePeriodDays) : null,
    }),
    onSuccess: () => {
      toast.success('Subscription created — status PENDING')
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not create subscription')),
  })

  const canSubmit = customerId !== '' && name && commitmentStart

  return (
    <Modal
      title="New subscription"
      subtitle="Created in PENDING — Finance activates it before invoicing."
      width={640}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand"
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Creating…' : 'Create subscription'}
          </button>
        </>
      }
    >
      <FieldRow label="Customer">
        <select className="input" value={customerId} onChange={e => setCustomerId(Number(e.target.value) || '')}>
          <option value="">— pick —</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </FieldRow>
      <FieldRow label="Name" hint='e.g. "Standard 2026"'>
        <input className="input" value={name} onChange={e => setName(e.target.value)} />
      </FieldRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Commitment start">
          <input type="date" className="input" value={commitmentStart} onChange={e => setCommitmentStart(e.target.value)} />
        </FieldRow>
        <FieldRow label="Commitment end (optional)">
          <input type="date" className="input" value={commitmentEnd} onChange={e => setCommitmentEnd(e.target.value)} />
        </FieldRow>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <FieldRow label="Billing cycle">
          <select className="input" value={billingCycle} onChange={e => setBillingCycle(e.target.value)}>
            <option>MONTHLY</option><option>QUARTERLY</option><option>ANNUAL</option>
          </select>
        </FieldRow>
        <FieldRow label="Billing mode">
          <select className="input" value={billingMode} onChange={e => setBillingMode(e.target.value)}>
            <option>ARREARS</option><option>ADVANCE</option>
          </select>
        </FieldRow>
        <FieldRow label="Invoice grouping">
          <select className="input" value={invoiceGrouping} onChange={e => setInvoiceGrouping(e.target.value)}>
            <option value="ONE_INVOICE">One invoice</option>
            <option value="SEPARATE_PER_PRODUCT_TYPE">Per product type</option>
          </select>
        </FieldRow>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Contract reference (optional)">
          <input className="input" value={contractReference} onChange={e => setContractReference(e.target.value)} />
        </FieldRow>
        <FieldRow label="Notice period (days, optional)">
          <input type="number" min="0" className="input" value={noticePeriodDays} onChange={e => setNoticePeriodDays(e.target.value)} />
        </FieldRow>
      </div>
    </Modal>
  )
}


// ── DiscountRuleEditorModal — POST/PATCH /discount-rules + live preview ────


export function DiscountRuleEditorModal({
  rule, onClose,
}: { rule: DiscountRule | null; onClose: () => void }) {
  const qc = useQueryClient()
  const editing = rule != null

  const [name,        setName]        = useState<string>(rule?.name ?? '')
  const [description, setDescription] = useState<string>(rule?.description ?? '')
  const [ruleType,    setRuleType]    = useState<DiscountRuleType>(rule?.ruleType ?? 'VOLUME_TIER')
  const [flatPercent, setFlatPercent] = useState<string>(rule?.flatPercent?.toString() ?? '')
  const [flatAmount,  setFlatAmount]  = useState<string>(rule?.flatAmount?.toString()  ?? '')
  const [flatCurrency,setFlatCurrency]= useState<string>(rule?.flatAmountCurrency ?? 'SEK')
  type Band = { fromCount: string; toCountInclusive: string; discountPercent: string }
  const [bands, setBands] = useState<Band[]>(
    rule?.tierBands?.map(b => ({
      fromCount:        b.fromCount.toString(),
      toCountInclusive: b.toCountInclusive?.toString() ?? '',
      discountPercent:  b.discountPercent.toString(),
    })) ?? [
      { fromCount: '1',  toCountInclusive: '10',  discountPercent: '0'  },
      { fromCount: '11', toCountInclusive: '20',  discountPercent: '10' },
      { fromCount: '21', toCountInclusive: '',    discountPercent: '20' },
    ],
  )

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name, description: description || null, ruleType }
      if (ruleType === 'PERCENTAGE')   body.flatPercent = Number(flatPercent)
      if (ruleType === 'FIXED_AMOUNT') { body.flatAmount = Number(flatAmount); body.flatAmountCurrency = flatCurrency }
      if (ruleType === 'VOLUME_TIER')  body.tierBands = bands.map(b => ({
        fromCount:        Number(b.fromCount),
        toCountInclusive: b.toCountInclusive === '' ? null : Number(b.toCountInclusive),
        discountPercent:  Number(b.discountPercent),
      }))
      if (editing) return api.patch(`/discount-rules/${rule?.id}`, body)
      return api.post('/discount-rules', body)
    },
    onSuccess: () => {
      toast.success(editing ? 'Rule updated' : 'Rule created')
      qc.invalidateQueries({ queryKey: ['discount-rules'] })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not save rule')),
  })

  // Live preview — only runs while editing an existing VOLUME_TIER rule, so we
  // have a real rule id to POST to. For a brand-new rule the preview is purely
  // local and shown after the first save.
  const [previewCount, setPreviewCount] = useState<string>('45')
  const [previewPrice, setPreviewPrice] = useState<string>('950')
  const { data: preview } = useQuery({
    queryKey: ['discount-rule-preview', rule?.id, previewCount, previewPrice],
    enabled:  editing && ruleType === 'VOLUME_TIER' && previewCount !== '' && previewPrice !== '',
    queryFn:  () => api.post(`/discount-rules/${rule?.id}/preview`, {
      companyCount: Number(previewCount),
      unitPrice:    Number(previewPrice),
    }) as Promise<{ gross: number; totalDiscount: number; net: number; bands: { fromCount: number; toCountInclusive: number | null; discountPercent: number; unitsInBand: number; discountAmount: number }[] }>,
  })

  function updateBand(i: number, field: keyof Band, value: string) {
    setBands(bs => bs.map((b, idx) => idx === i ? { ...b, [field]: value } : b))
  }

  function addBand() {
    const last = bands[bands.length - 1]
    const startFrom = last.toCountInclusive ? String(Number(last.toCountInclusive) + 1) : ''
    setBands([...bands.slice(0, -1),
      { ...last, toCountInclusive: startFrom ? String(Number(startFrom) - 1) : last.toCountInclusive },
      { fromCount: startFrom, toCountInclusive: '', discountPercent: '0' },
    ])
  }

  return (
    <Modal
      title={editing ? `Edit ${rule?.name}` : 'New discount rule'}
      subtitle={editing ? `Used by ${rule?.usedByCount ?? 0} subscriptions` : undefined}
      width={700}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand"
            disabled={mutation.isPending || !name}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving…' : (editing ? 'Save changes' : 'Create rule')}
          </button>
        </>
      }
    >
      <FieldRow label="Name">
        <input className="input" value={name} onChange={e => setName(e.target.value)} />
      </FieldRow>
      <FieldRow label="Description (optional)">
        <textarea className="input" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
      </FieldRow>
      <FieldRow label="Rule type" hint={editing ? 'Type cannot change — archive + create a new rule instead.' : undefined}>
        <select className="input" value={ruleType} disabled={editing}
          onChange={e => setRuleType(e.target.value as DiscountRuleType)}>
          <option value="VOLUME_TIER">Volume tier</option>
          <option value="PERCENTAGE">Flat percent</option>
          <option value="FIXED_AMOUNT">Fixed amount</option>
        </select>
      </FieldRow>

      {ruleType === 'PERCENTAGE' && (
        <FieldRow label="Flat discount percent">
          <input type="number" min="0" max="100" step="0.01" className="input"
            value={flatPercent} onChange={e => setFlatPercent(e.target.value)} />
        </FieldRow>
      )}

      {ruleType === 'FIXED_AMOUNT' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <FieldRow label="Flat amount per month">
            <input type="number" min="0" step="0.01" className="input"
              value={flatAmount} onChange={e => setFlatAmount(e.target.value)} />
          </FieldRow>
          <FieldRow label="Currency">
            <input className="input" maxLength={3}
              value={flatCurrency} onChange={e => setFlatCurrency(e.target.value.toUpperCase())} />
          </FieldRow>
        </div>
      )}

      {ruleType === 'VOLUME_TIER' && (
        <>
          <div>
            <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Tier bands</div>
            <table className="table" style={{ fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th>From</th>
                  <th>To (blank = and above)</th>
                  <th>Discount %</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b, i) => (
                  <tr key={i}>
                    <td><input className="input" style={{ height: 28 }} value={b.fromCount} onChange={e => updateBand(i, 'fromCount', e.target.value)} /></td>
                    <td><input className="input" style={{ height: 28 }} value={b.toCountInclusive} onChange={e => updateBand(i, 'toCountInclusive', e.target.value)} /></td>
                    <td><input type="number" min="0" max="100" step="0.01" className="input" style={{ height: 28 }} value={b.discountPercent} onChange={e => updateBand(i, 'discountPercent', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn btn-sm" onClick={addBand} style={{ marginTop: 6 }}>+ Add band</button>
          </div>

          {editing && (
            <div className="card" style={{ padding: 12, background: 'var(--ns-brand-soft)' }}>
              <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Live preview</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                <FieldRow label="Lines">
                  <input className="input" type="number" min="0" step="0.01" value={previewCount} onChange={e => setPreviewCount(e.target.value)} />
                </FieldRow>
                <FieldRow label="Unit price">
                  <input className="input" type="number" min="0" step="0.01" value={previewPrice} onChange={e => setPreviewPrice(e.target.value)} />
                </FieldRow>
                <div style={{ alignSelf: 'end', fontSize: 12.5, fontWeight: 600 }}>
                  <div>Gross: <span className="tabular">{preview?.gross.toFixed(2) ?? '—'}</span></div>
                  <div style={{ color: 'var(--ns-ok)' }}>Discount: <span className="tabular">−{preview?.totalDiscount.toFixed(2) ?? '—'}</span></div>
                  <div>Net: <span className="tabular">{preview?.net.toFixed(2) ?? '—'}</span></div>
                </div>
              </div>
              {preview && preview.bands.length > 0 && (
                <div style={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace' }}>
                  {preview.bands.map((b, i) => (
                    <div key={i}>
                      {b.fromCount}–{b.toCountInclusive ?? '∞'} ({b.discountPercent}%): {b.unitsInBand.toFixed(2)} units → −{b.discountAmount.toFixed(2)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}


// ── NewProductTypeModal — POST /product-types ──────────────────────────────


export function NewProductTypeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [code,        setCode]        = useState('')
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder,   setSortOrder]   = useState('100')

  const mutation = useMutation({
    mutationFn: async () => api.post('/product-types', {
      code:        code.trim().toUpperCase(),
      displayName: displayName.trim() || code.trim().toUpperCase(),
      description: description.trim() || null,
      sortOrder:   Number(sortOrder) || 100,
    }),
    onSuccess: () => {
      toast.success(`Product type "${code.trim().toUpperCase()}" added`)
      qc.invalidateQueries({ queryKey: ['product-types'] })
      qc.invalidateQueries({ queryKey: ['articles']      })
      onClose()
    },
    onError: e => toast.error(mutationError(e, 'Could not add product type')),
  })

  const codeValid = /^[A-Z0-9_]+$/i.test(code.trim()) && code.trim().length > 0

  return (
    <Modal
      title="New product type"
      subtitle="Adds a code to the managed list. Articles can be classified under it from the Articles tab."
      width={520}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand"
            disabled={!codeValid || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving…' : 'Add product type'}
          </button>
        </>
      }
    >
      <FieldRow label="Code" hint="Short uppercase identifier, e.g. SYNC, REPORT, API. Alphanumeric + underscores.">
        <input
          className="input mono"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          maxLength={40}
          autoFocus
        />
      </FieldRow>
      <FieldRow label="Display name (optional)" hint="Defaults to the code if blank.">
        <input
          className="input"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder={code.trim() || 'Clementine Sync'}
        />
      </FieldRow>
      <FieldRow label="Description (optional)">
        <textarea
          className="input"
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Sort order" hint="Lower numbers sort first in pickers.">
        <input
          type="number" min="0" step="1"
          className="input"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
        />
      </FieldRow>
    </Modal>
  )
}
